import './style.modules.scss'

import { BrandIcon, BrandIconId, Loading } from '@ampcore/adel-react'
import {
  AdvisorDataService,
  FaceProfileData
} from '@ampcore/client-side-services'
import { sanitizeUrl } from '@ampcore/string-format'
import Cookies from 'js-cookie'
import React, { useEffect, useState } from 'react'
import xss from 'xss'

export type AdvisorProfileProps = {
  /** Pre-fetched face profile data; skips internal fetch when provided */
  faceProfileData?: FaceProfileData
  /** Advisor ID to fetch; falls back to FaceProfileCookie when omitted */
  advisorId?: string
  /** Injected loading state; overrides internal state when provided */
  loading?: boolean
  /** Injected error; overrides internal state when provided */
  error?: Error | null
  testId?: string
}

export const AdvisorProfile: React.FC<AdvisorProfileProps> = ({
  faceProfileData: propsFaceProfileData,
  advisorId,
  loading: propsLoading,
  error: propsError,
  testId
}) => {
  const [internalFaceProfileData, setInternalFaceProfileData] = useState<
    FaceProfileData | undefined
  >()
  // start as true so the spinner shows immediately; skip when props are injected
  const [internalLoading, setInternalLoading] = useState(
    propsFaceProfileData === undefined &&
      propsLoading === undefined &&
      propsError === undefined
  )
  const [internalError, setInternalError] = useState<Error | null>(null)

  const faceProfileData = propsFaceProfileData ?? internalFaceProfileData
  const loading = propsLoading ?? internalLoading
  const error = propsError ?? internalError

  useEffect(() => {
    if (
      propsFaceProfileData !== undefined ||
      propsLoading !== undefined ||
      propsError !== undefined
    ) {
      return
    }

    const id = advisorId ?? Cookies.get('FaceProfileCookie')
    if (!id) {
      setInternalLoading(false)
      return
    }

    const fetchData = async () => {
      setInternalLoading(true)
      setInternalError(null)
      try {
        const service = new AdvisorDataService()
        const response = await service.getById(id)
        setInternalFaceProfileData(response.data ?? undefined)
      } catch (e) {
        setInternalError(e instanceof Error ? e : new Error('Unknown error'))
      } finally {
        setInternalLoading(false)
      }
    }

    fetchData()
  }, [advisorId, propsFaceProfileData, propsLoading, propsError])

  useEffect(() => {
    if (!faceProfileData?.name) {
      return
    }

    const updateDom = (): boolean => {
      const advisorElt = document.querySelector(
        '#advisorText'
      ) as HTMLElement | null

      const resultsElt = document.querySelector(
        '#rollover-results-download-content-pdf'
      ) as HTMLElement | null

      if (!advisorElt || !resultsElt) {
        return false
      }

      // Prevent duplicate updates
      if (
        advisorElt.dataset.advisorUpdated === 'true' &&
        resultsElt.dataset.advisorUpdated === 'true'
      ) {
        return true
      }

      advisorElt.innerHTML = advisorElt.innerHTML.replace(
        '%advisor%',
        faceProfileData.name ?? 'Your advisor'
      )

      resultsElt.innerHTML = resultsElt.innerHTML.replace(
        '%advisor%',
        faceProfileData.name ?? 'Your advisor'
      )

      advisorElt.dataset.advisorUpdated = 'true'
      resultsElt.dataset.advisorUpdated = 'true'

      if (!faceProfileData.email) {
        const emailBtn = document.querySelector(
          '.rollover-results-summary .Button--primary'
        ) as HTMLElement | null

        emailBtn?.classList.add('u-hidden')
      }

      return true
    }

    // Handle case where elements already exist
    if (updateDom()) {
      return
    }

    const container = document.querySelector(
      '.rollover-component'
    ) as HTMLElement | null

    if (!container) {
      return
    }

    const observer = new MutationObserver(() => {
      if (updateDom()) {
        observer.disconnect()
      }
    })

    observer.observe(container, {
      childList: true,
      subtree: true
    })

    return () => observer.disconnect()
  }, [faceProfileData])

  useEffect(() => {
    if (!faceProfileData) return

    window.dispatchEvent(
      new CustomEvent<FaceProfileData>('profile-loaded', {
        detail: faceProfileData
      })
    )
  }, [faceProfileData])

  if (loading) {
    return (
      <div className="AdvisorProfile u-sizeConstrained" data-testid={testId}>
        <div className="u-floatRight u-size2of4 u-md-size2of4 u-sm-sizeFull u-posRelative u-padding48">
          <Loading
            className="u-posAbsoluteCenter u-textColorBrand"
            testId={testId}
          />
        </div>
      </div>
    )
  }

  const profileUrl =
    faceProfileData?.urls?.site ?? faceProfileData?.urls?.referme

  const iconMap: Record<'twitter' | 'facebook' | 'linkedin', BrandIconId> = {
    twitter: BrandIconId.SOCIAL_X,
    facebook: BrandIconId.SOCIAL_FACEBOOK,
    linkedin: BrandIconId.SOCIAL_LINKEDIN
  }

  const socialIcons = faceProfileData
    ? (Object.keys(iconMap) as Array<keyof typeof iconMap>)
        .map(key =>
          faceProfileData.social[key] ? (
            <li
              key={key}
              className="u-textSizeMsrp1 u-flexJustifyCenter u-inlineBlock u-marginRight8"
            >
              <a
                href={sanitizeUrl(xss(faceProfileData.social[key] ?? ''))}
                target="_blank"
                rel="noopener noreferrer"
                className="AdvisorProfile-socialIcon Icon-link u-inlineBlock"
                data-testid={testId ? `${testId}-social-${key}` : undefined}
              >
                <BrandIcon
                  icon={iconMap[key]}
                  ariaLabel={key}
                  style={{ width: '20px', height: '20px' }}
                />
              </a>
            </li>
          ) : null
        )
        .filter(Boolean)
    : []

  if (error != null || !faceProfileData) {
    return null
  }

  return (
    <div
      id="advisor-profile"
      className="AdvisorProfile u-sizeConstrained"
      data-analytics-container="Advisor"
      data-testid={testId}
    >
      <div className="u-floatRight u-size2of4 u-md-size2of4 u-sm-sizeFull">
        <div className="u-flex u-flexRow u-sm-flexCol u-paddingVert20">
          <div className="AdvisorProfile-imageWrapper u-marginRight20 u-sm-marginRight0 u-sm-marginBottom20 u-size1of4 u-md-size1of3 u-sm-size1of3">
            <img
              className="Advisor-image u-maxWidth100"
              src={sanitizeUrl(
                xss(
                  faceProfileData?.image ??
                    `${window.AMPCOMPATH}images/dac-missing-image.png`
                )
              )}
              alt={xss(faceProfileData?.name ?? '')}
              data-testid={testId ? `${testId}-image` : undefined}
            />
          </div>
          <div className="AdvisorProfile-info">
            <a
              className="AdvisorProfile-link u-linkClean"
              href={sanitizeUrl(xss(profileUrl ?? '/'))}
              target="_blank"
              rel="noopener noreferrer"
              style={!profileUrl ? { pointerEvents: 'none' } : undefined}
              data-testid={testId ? `${testId}-link` : undefined}
            >
              <span
                className="AdvisorClient-name u-textSizeMsrp1 u-textSecondary u-marginBottom4 u-inlineBlock"
                data-testid={testId ? `${testId}-name` : undefined}
              >
                {xss(faceProfileData?.name ?? 'Your advisor')}
              </span>
            </a>
            {faceProfileData?.title && (
              <p
                className="u-marginBottom8 u-textBreak"
                data-testid={testId ? `${testId}-title` : undefined}
              >
                {xss(faceProfileData.title)}
              </p>
            )}
            {faceProfileData?.email && (
              <p className="u-marginBottom8">
                <a
                  className="u-textBreak u-textColorDefault"
                  href={sanitizeUrl(`mailto:${xss(faceProfileData.email)}`)}
                  data-testid={testId ? `${testId}-email` : undefined}
                >
                  {xss(faceProfileData.email)}
                </a>
              </p>
            )}
            {faceProfileData?.phone && (
              <p className="u-marginBottom8">
                <a
                  className="u-textBreak u-textColorDefault"
                  href={sanitizeUrl(
                    `tel:${xss(faceProfileData.phone.replace(/\s+/g, ''))}`
                  )}
                  data-testid={testId ? `${testId}-phone` : undefined}
                >
                  {xss(faceProfileData.phone)}
                </a>
              </p>
            )}
            {socialIcons.length > 0 && (
              <ul className="u-border0 u-marginLeft0 u-flex">{socialIcons}</ul>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
